import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterLinkWithHref } from '@angular/router';
import { Organisation } from '../../../../models/organisation.model';
import { OrganisationsMappingService } from '@Services';
import { NgClass } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { LoadingSpinnerComponent } from '../../../loading-spinner/loading-spinner.component';
import { ConfirmationDialogueComponent } from '../../../confirmation-dialogue/confirmation-dialogue.component';
import { orgMappingValidator } from '@Utils';

@Component({
  selector: 'app-org-mapping-editor',
  standalone: true,
  imports: [
    NgClass,
    RouterLinkWithHref,
    FormsModule,
    LoadingSpinnerComponent,
    ConfirmationDialogueComponent,
    ReactiveFormsModule,
  ],
  templateUrl: './org-mapping-editor.component.html',
  styleUrl: './org-mapping-editor.component.css',
})
export class OrgMappingEditorComponent implements OnInit {
  organisationForm: FormGroup;
  formSubmitted = false;

  isEditing = false;
  showConfirmDialog = false;

  @ViewChild('pageTitle') pageTitle!: ElementRef<HTMLHeadingElement>;
  alertType: 'success' | 'danger' | 'info' = 'info';

  isAlertVisible = false;
  alertMessage = '';

  isSaving = false;
  isLoading = false;

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _orgMappingService: OrganisationsMappingService,
    private _fb: FormBuilder,
  ) {
    this.organisationForm = this._fb.group({
      orgMappingGuid: [''],
      key: [''],
      orgCode: ['', Validators.required],
      type: ['-1', orgMappingValidator()],
      authorityType: ['-1', orgMappingValidator()],
      value: [''],
      contract: ['-1', orgMappingValidator()],
      area: ['', Validators.required],
      DataSourceId: ['-1', orgMappingValidator()],
    });
  }

  ngOnInit(): void {
    const id = this._route.snapshot.paramMap.get('id');
    if (id) {
      console.log('id', id);
      this.isEditing = true;
      this.loadOrganisation(id);
    }
  }

  loadOrganisation(id: string): void {
    this.isLoading = true;
    this._orgMappingService.getWhatsNewSectionByGuid(id).subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response) {
          this.organisationForm.patchValue({
            orgMappingGuid: response.orgMappingGuid,
            key: response.key,
            orgCode: response.orgCode,
            type: response.type,
            authorityType: response.authorityType,
            value: response.value,
            contract: response.contract,
            area: response.area,
            DataSourceId: response.dataSourceId,
          });

          if (this.pageTitle) {
            this.pageTitle.nativeElement.textContent = `Edit Organisation: ${response.orgCode}`;
          }
        } else {
          this.showAlert('Organisation not found!', 'danger');
          console.log('Organisation not found!');
        }
      },
      error: (error) => {
        this.isLoading = false;
        this.showAlert('Error loading organisation data', 'danger');
        console.error('Error fetching organisation:', error);
      },
    });
  }

  onSubmit() {
    this.formSubmitted = true;
    this.focusTitle();

    if (this.organisationForm.value.type === '-1') {
      return this.showAlert('The Role Type is invalid', 'danger');
    }

    this.organisationForm.value['DataSourceId'] = Number(this.organisationForm.value['DataSourceId']);

    if (this.organisationForm.value.type === 'GP' || this.organisationForm.value.type === 'PD') {
      this.organisationForm.value['key'] = this.organisationForm.value['orgCode'];
      this.organisationForm.value['value'] = this.organisationForm.value['orgCode'];
    }
    const organisation: Organisation = this.organisationForm.value;
    if (this.organisationForm.valid) {
      if (this.isEditing) {
        return this._orgMappingService.updateOrganisation(organisation).subscribe({
          next: () => {
            this.isSaving = false;
            this.isEditing = true;
            this.isAlertVisible = true;
            this.focusTitle();
            this.showAlert('Organisation updated successfully', 'success');
          },
          error: (error) => {
            this.isSaving = false;
            console.log('error', error);
          },
        });
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { orgMappingGuid, ...requestBody } = organisation;
      return this._orgMappingService.addOrganisation(requestBody).subscribe({
        next: (response) => {
          this.isSaving = false;
          this.organisationForm.value['orgMappingGuid'] = response.guid;
          this.isEditing = true;
          this.isAlertVisible = true;
          this.showAlert('Organisation added successfully');
        },
        error: (error) => {
          this.isSaving = false;
          this.showAlert('Error', 'danger');
          console.log('error', error);
        },
      });
    } else {
      this.markFormGroupTouched(this.organisationForm);
      return this.showAlert('The Organisation is invalid', 'danger');
    }
  }

  markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  onCancel() {
    this._router.navigate(['admin/organisations-mapping']).then();
  }

  onDelete() {
    this.showConfirmDialog = true;
  }

  onConfirmDelete() {
    this.showConfirmDialog = false;
    this._orgMappingService.deleteOrganisation(this.organisationForm.value['orgMappingGuid']).subscribe({
      next: () => {
        console.log('Item deleted successfully');
        this._router.navigate(['admin/organisations-mapping']).then(() => {
          console.log('Navigated back to Organisation list');
        });
      },
      error: (err) => {
        console.error('Failed to delete item', err);
      },
    });
  }

  onCancelDelete() {
    this.showConfirmDialog = false;
  }

  showAlert(message: string, type: 'success' | 'danger' | 'info' = 'info') {
    this.alertMessage = message.trim();
    this.alertType = type;
    this.isAlertVisible = true;

    // Hide the alert after 8 seconds
    setTimeout(() => {
      this.isAlertVisible = false;
    }, 8000);
  }

  dismissAlert(): void {
    this.isAlertVisible = false;
    this.alertMessage = '';
  }

  focusTitle() {
    if (this.pageTitle) {
      this.pageTitle.nativeElement.focus();
      requestAnimationFrame(() => {
        this.pageTitle.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }
}
